/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';

import { useProductAdminContext } from './ProductAdminContext';
import ViewDetail from '@react/react-spectrum/Icon/ViewDetail';
import { defineMessages, useIntl } from 'react-intl';

import { ConfigCard, CategoryCard, ProductCard, QuickActionWrapper } from '../Cards';
import { trackEvent } from '../../utils/tracking';
import {
    event_productadmin_configcard,
    event_productadmin_categorycard,
    event_productadmin_productcard,
    event_productadmin_productproperties,
    event_productadmin_categoryproperties,
    event_productadmin_configproperties
} from '../../utils/trackingEvents';
import { properties_product, properties_category, properties_config } from './urls';
import { mapBreadcrumb } from './utils';
import { useNavigate } from '../common/useNavigate';

const messages = defineMessages({
    actionViewDetails: {
        id: 'action.viewDetails',
        defaultMessage: 'View Details'
    },
    failedToLoadSchema: {
        id: 'error.failedLoadingSchema',
        defaultMessage: 'Unable to load schema.'
    }
});

const createViewDetailAction = (action, label, disabled = false) => ({
    label,
    icon: <ViewDetail alt={label} />,
    action: e => {
        e.preventDefault();
        e.stopPropagation();
        action();
    },
    disabled
});

const AdminConfigCardWrapper = React.forwardRef((props, ref) => {
    const [, { setConfig }] = useProductAdminContext();
    const { formatMessage } = useIntl();
    const { item, ...otherProps } = props;
    const { location } = useNavigate();

    const forwardedProps = {
        ...otherProps,
        ref
    };

    return (
        <QuickActionWrapper
            actions={[
                createViewDetailAction(() => {
                    trackEvent(event_productadmin_configproperties);
                    location(properties_config(item.path));
                }, formatMessage(messages.actionViewDetails))
            ]}
            {...forwardedProps}
        >
            <ConfigCard
                errorMessage={!item.schemaVersion ? formatMessage(messages.failedToLoadSchema) : undefined}
                onClick={
                    item.schemaVersion
                        ? () => {
                              trackEvent(event_productadmin_configcard);
                              setConfig(item);
                          }
                        : undefined
                }
                config={item}
            />
        </QuickActionWrapper>
    );
});
AdminConfigCardWrapper.displayName = 'AdminConfigCard';
AdminConfigCardWrapper.propTypes = {
    item: PropTypes.object.isRequired
};
export const AdminConfigCard = AdminConfigCardWrapper;

const AdminCategoryCardWrapper = React.forwardRef((props, ref) => {
    const [{ config }, { setCategories, navigate }] = useProductAdminContext();
    const { formatMessage } = useIntl();
    const { item, ...otherProps } = props;
    const forwardedProps = {
        ...otherProps,
        ref
    };

    const onClick = () => {
        const breadcrumbs = item.breadcrumbs || [];
        const path = [...breadcrumbs.map(mapBreadcrumb), item];
        trackEvent(event_productadmin_categorycard);
        setCategories(path);
    };

    return (
        <QuickActionWrapper
            actions={[
                createViewDetailAction(() => {
                    trackEvent(event_productadmin_categoryproperties);
                    navigate(properties_category(config.identifier, item.identifier));
                }, formatMessage(messages.actionViewDetails))
            ]}
            {...forwardedProps}
        >
            <CategoryCard onClick={onClick} category={item} />
        </QuickActionWrapper>
    );
});
AdminCategoryCardWrapper.displayName = 'AdminCategoryCard';
AdminCategoryCardWrapper.propTypes = {
    item: PropTypes.object.isRequired
};
export const AdminCategoryCard = AdminCategoryCardWrapper;

const AdminProductCardWrapper = React.forwardRef((props, ref) => {
    const [{ config }, { setConfigurableProduct, navigate }] = useProductAdminContext();
    const { formatMessage } = useIntl();
    const { item, ...otherProps } = props;
    const forwardedProps = {
        ...otherProps,
        ref
    };

    let cardProps = {};
    if (item.__typename === 'ConfigurableProduct') {
        cardProps.onClick = () => {
            trackEvent(event_productadmin_productcard);
            setConfigurableProduct(item);
        };
    }

    return (
        <QuickActionWrapper
            actions={[
                createViewDetailAction(() => {
                    trackEvent(event_productadmin_productproperties);
                    navigate(properties_product(config.identifier, item.sku, item.base_product_sku));
                }, formatMessage(messages.actionViewDetails))
            ]}
            {...forwardedProps}
        >
            <ProductCard {...cardProps} product={item} />
        </QuickActionWrapper>
    );
});
AdminProductCardWrapper.displayName = 'AdminProductCard';
AdminProductCardWrapper.propTypes = {
    item: PropTypes.object.isRequired
};
export const AdminProductCard = AdminProductCardWrapper;
